// Thin WebSocket client wrapper with auto-reconnect, shared by the BookDetail co-presence
// indicator. Talks to the WebSocket layer added in server.js (presenceByBiblioId).

let socket = null;
let reconnectTimer = null;
let retryCount = 0;
const listeners = new Set();
const pendingQueue = [];

const getWsUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

const notifyListeners = (message) => {
  listeners.forEach((listener) => listener(message));
};

const flushPending = () => {
  while (pendingQueue.length > 0 && socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(pendingQueue.shift()));
  }
};

const connect = () => {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  socket = new WebSocket(getWsUrl());

  socket.addEventListener('message', (event) => {
    try {
      notifyListeners(JSON.parse(event.data));
    } catch (_error) {
      // Ignore malformed messages.
    }
  });

  socket.addEventListener('open', () => {
    retryCount = 0;
    flushPending();
  });

  socket.addEventListener('close', () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    // Exponential backoff with jitter: 1–6 s, doubling each retry, capped at 60 s.
    // Spreading reconnects randomly prevents a thundering-herd spike after server restarts.
    const base = Math.min(1000 * 2 ** retryCount, 60000);
    const jitter = Math.random() * 3000;
    retryCount += 1;
    reconnectTimer = setTimeout(connect, base + jitter);
  });

  socket.addEventListener('error', () => {
    socket?.close();
  });
};

const send = (payload) => {
  // Always reference the module-level `socket` rather than capturing it at call time — if a
  // reconnect is in progress the captured reference would be the dead old socket and the 'open'
  // listener would never fire, silently dropping the message.
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
  } else {
    pendingQueue.push(payload);
    connect();
  }
};

export function subscribeToRealtime(listener) {
  listeners.add(listener);
  connect();
  return () => {
    listeners.delete(listener);
  };
}

export function joinBiblioPresence(biblioId) {
  send({ type: 'join', biblioId: String(biblioId) });
}

export function leaveBiblioPresence(biblioId) {
  send({ type: 'leave', biblioId: String(biblioId) });
}
